import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Sensor } from '../../services/sensors';
import StatusBadge from '../Shared/StatusBadge';
import HistoryChart from './HistoryChart';
import DepthProfileChart from './DepthProfileChart';
import { PARAM_CONFIG, ParamKey, getDepthThreshold, depthsFromMetrics } from '../../data/demo';
import { useSensorHistory, toSeries } from '../../hooks/useSensorHistory';
import { ParamIcon } from './ParamIcon';
import { formatLectura, esTopeDeSensor } from '../../utils/soilReading';

type Tab = 'overview' | 'depthProfile' | 'heatMap' | 'historical';
type HistoryRange = 'day' | 'week' | 'month' | 'year';

interface Props {
  sensor: Sensor;
  displayName?: string;
  initialParam?: ParamKey;
  onClose: () => void;
  /** Guarda en el servidor; la promesa se rechaza si no se pudo. */
  onRename?: (name: string) => Promise<void>;
}

function getStatus(value: number, lo: number, hi: number): 'good' | 'warn' | 'alert' {
  const margin = (hi - lo) * 0.15;
  if (value < lo - margin || value > hi + margin) return 'alert';
  if (value < lo || value > hi) return 'warn';
  return 'good';
}

const STATUS_COLOR = {
  good:  'var(--green)',
  warn:  'var(--amber)',
  alert: 'var(--red)',
};

// Resolves the exact calendar window for a range anchored on a specific
// date — e.g. 'week' always means Monday–Sunday of the week containing
// `anchor`, not "7 days ending today". This is what lets the picker jump
// to any past period instead of only ever showing a window ending now.
function getRangeBounds(range: HistoryRange, anchor: Date): { start: Date; end: Date } {
  const y = anchor.getFullYear(), m = anchor.getMonth(), d = anchor.getDate();
  if (range === 'day') {
    return { start: new Date(y, m, d, 0, 0, 0, 0), end: new Date(y, m, d, 23, 59, 59, 999) };
  }
  if (range === 'week') {
    const dow = anchor.getDay(); // 0=Sun..6=Sat
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(y, m, d + mondayOffset, 0, 0, 0, 0);
    const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6, 23, 59, 59, 999);
    return { start: monday, end: sunday };
  }
  if (range === 'month') {
    return { start: new Date(y, m, 1, 0, 0, 0, 0), end: new Date(y, m + 1, 0, 23, 59, 59, 999) };
  }
  // year
  return { start: new Date(y, 0, 1, 0, 0, 0, 0), end: new Date(y, 11, 31, 23, 59, 59, 999) };
}

function toDateInputValue(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function toMonthInputValue(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function parseDateInput(value: string): Date {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function parseMonthInput(value: string): Date {
  const [y, m] = value.split('-').map(Number);
  return new Date(y, m - 1, 1);
}
function formatPeriodLabel(range: HistoryRange, start: Date, end: Date, locale: string): string {
  if (range === 'day') return start.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  if (range === 'year') return start.toLocaleDateString(locale, { year: 'numeric' });
  if (range === 'month') return start.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  // week
  const startLabel = start.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  const endLabel = end.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startLabel} – ${endLabel}`;
}

export default function SensorModal({ sensor, displayName, initialParam = 'humidity', onClose, onRename }: Props) {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [selectedParam, setSelectedParam] = useState<ParamKey>(initialParam);
  const [historyRange, setHistoryRange] = useState<HistoryRange>('week');
  const [anchorDate, setAnchorDate] = useState<Date>(() => new Date());
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameDraft, setRenameDraft] = useState(displayName ?? sensor.name);
  /**
   * Antes se ponía en "✓ Saved" sin esperar nada, porque el nombre se
   * guardaba en localStorage y no podía fallar. Ahora va al servidor, así que
   * el botón tiene que reflejar los tres estados de verdad — decir "guardado"
   * cuando no se guardó es peor que no decir nada.
   */
  const [renameEstado, setRenameEstado] = useState<'quieto' | 'guardando' | 'guardado' | 'fallo'>('quieto');

  const handleRename = async () => {
    if (!onRename || !renameDraft.trim()) return;
    setRenameEstado('guardando');
    try {
      await onRename(renameDraft.trim());
      setRenameEstado('guardado');
      setTimeout(() => { setRenameEstado('quieto'); setRenameOpen(false); }, 1500);
    } catch {
      setRenameEstado('fallo');
    }
  };

  const metrics = sensor.lastReading?.metrics ?? {};
  const paramCfg = PARAM_CONFIG.find(p => p.key === selectedParam)!;

  // Un device dado de alta que todavía no reporta no tiene métricas. Sin este
  // caso, todas las tarjetas caían al `?? 0` y salían "0.0" con badge ALERT en
  // rojo — nueve mediciones inventadas, indistinguibles de un sensor que de
  // verdad está midiendo cero. Mismo problema ya corregido en HomePage y
  // SoilOverview (commit 296fc5c); este modal se había quedado fuera.
  const awaitingData = !sensor.lastReading;

  // El eje de profundidad sale de las métricas del propio sensor: un Xiaoma
  // mide donde diga su portMap (10–70 cm en el piloto), no en las 7 capas
  // fijas del hardware Yantai que estaban cableadas antes.
  const depths = depthsFromMetrics(metrics);

  // Depth values for the selected param
  // null y NO 0 en las profundidades sin sensor: un hueco no es una medición
  // de cero. Ver el comentario de Props en DepthProfileChart.
  const depthValues = depths.map(d => metrics[`${selectedParam}_${d}`] ?? null);

  /**
   * Histórico REAL, desde la base, para la ventana de calendario elegida.
   *
   * Antes venía de getDemoParamHistory(), que buscaba el imei en la lista de
   * sensores demo del frontend. Para un sensor real nunca lo encontraba, caía
   * a un objeto vacío y promediaba cero: la pestaña mostraba una línea plana
   * en 0.0 con Average/Min/Max en 0.0 aunque hubiera meses de telemetría
   * guardada. Un cliente leía eso como "mi humedad promedio fue 0%".
   */
  const { start: rangeStart, end: rangeEnd } = getRangeBounds(historyRange, anchorDate);
  const { readings: histReadings, loading: histLoading, error: histError } = useSensorHistory(
    sensor.imei,
    { from: rangeStart.toISOString(), to: rangeEnd.toISOString() },
  );
  // Promedia todas las profundidades por instante: la pestaña grafica el
  // parámetro para el sensor completo, no para una capa.
  const historyData = useMemo(
    () => toSeries(histReadings, selectedParam),
    [histReadings, selectedParam],
  );

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview',     label: t('soil.modal.tabs.overview') },
    { key: 'depthProfile', label: t('soil.modal.tabs.depthProfile') },
    { key: 'heatMap',      label: t('soil.modal.tabs.heatMap') },
    { key: 'historical',   label: t('soil.modal.tabs.historical') },
  ];

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--white)', width: '100%', maxWidth: 860, maxHeight: '90vh',
          borderRadius: 16, boxShadow: '0 24px 40px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <div style={{ padding: '18px 24px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--dark)' }}>{displayName ?? sensor.name}</h2>
                <StatusBadge status={sensor.status} variant="device" />
                {onRename && (
                  <button
                    onClick={() => { setRenameOpen(o => !o); setRenameDraft(displayName ?? sensor.name); setRenameEstado('quieto'); }}
                    title={t('sensorModal.renameTitle')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: 4, lineHeight: 1, display: 'flex', alignItems: 'center', opacity: 0.4 }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '0.4'; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--dark)' }}>
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray)' }}>
                IMEI: {sensor.imei} &nbsp;·&nbsp; {Number(sensor.lat).toFixed(4)}, {Number(sensor.lng).toFixed(4)}
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--gray)', lineHeight: 1, marginLeft: 12 }}>&times;</button>
          </div>

          {/* Rename row */}
          {onRename && renameOpen && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '10px 12px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray)', whiteSpace: 'nowrap' }}>{t('sensorModal.displayName')}</span>
              <input
                value={renameDraft}
                onChange={e => { setRenameDraft(e.target.value); setRenameEstado('quieto'); }}
                onKeyDown={e => { if (e.key === 'Enter') handleRename(); }}
                style={{
                  flex: 1, fontSize: 13, border: '1.5px solid var(--border)', borderRadius: 6,
                  padding: '5px 10px', outline: 'none', color: 'var(--dark)', background: 'var(--white)',
                }}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--green)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
              />
              <button
                onClick={handleRename}
                disabled={renameEstado === 'guardando'}
                style={{
                  padding: '5px 16px', borderRadius: 6, border: 'none',
                  cursor: renameEstado === 'guardando' ? 'default' : 'pointer',
                  fontSize: 12, fontWeight: 600,
                  background: renameEstado === 'guardado' ? 'var(--green)'
                    : renameEstado === 'fallo' ? 'var(--red)' : 'var(--green-bg)',
                  color: renameEstado === 'guardado' || renameEstado === 'fallo' ? '#fff' : 'var(--green-dk)',
                  transition: 'all 0.2s', whiteSpace: 'nowrap',
                }}
              >
                {renameEstado === 'guardando' ? t('sensorModal.saving')
                  : renameEstado === 'guardado' ? t('sensorModal.saved')
                  : renameEstado === 'fallo' ? t('sensorModal.notSaved') : t('sensorModal.save')}
              </button>
            </div>
          )}
        </div>

        {/* ── Tabs ───────────────────────────────────────────── */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'var(--bg)' }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1, padding: '10px 0', border: 'none', background: 'transparent',
                fontSize: 12, fontWeight: activeTab === tab.key ? 600 : 400,
                color: activeTab === tab.key ? 'var(--green-dk)' : 'var(--gray)',
                borderBottom: activeTab === tab.key ? '2px solid var(--green)' : '2px solid transparent',
                cursor: 'pointer', transition: 'color 0.12s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Content ────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {awaitingData && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 260, textAlign: 'center', padding: '0 24px' }}>
              <div style={{ fontSize: 40, marginBottom: 14 }}>📡</div>
              {/* Dos situaciones muy distintas que antes se veían igual:
                  'offline' = nunca ha transmitido. Cualquier otro estado =
                  el device SÍ transmitió hace poco, pero ninguna de sus
                  lecturas trae profundidad asignada, así que no hay nada que
                  ubicar en esta vista. Lo segundo es un problema de
                  configuración (falta metadata.portMap del device), no una
                  falla del sensor — y decirlo ahorra horas de diagnóstico. */}
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--dark)', marginBottom: 8 }}>
                {sensor.status === 'offline' ? t('sensorModal.awaitingTitle') : t('sensorModal.unmappedTitle')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--gray)', lineHeight: 1.6, maxWidth: 440 }}>
                {sensor.status === 'offline' ? t('sensorModal.awaitingBody') : t('sensorModal.unmappedBody')}
              </div>
            </div>
          )}
          {!awaitingData && activeTab === 'overview'     && <OverviewTab    metrics={metrics} selectedParam={selectedParam} onSelectParam={setSelectedParam} depths={depths} />}
          {!awaitingData && activeTab === 'depthProfile' && <DepthProfileTab selectedParam={selectedParam} onSelectParam={setSelectedParam} paramCfg={paramCfg} depthValues={depthValues} depths={depths} />}
          {!awaitingData && activeTab === 'heatMap'      && <HeatMapTab     metrics={metrics} depths={depths} />}
          {!awaitingData && activeTab === 'historical'   && (
            <HistoricalTab
              loading={histLoading}
              error={histError}
              historyData={historyData}
              selectedParam={selectedParam}
              onSelectParam={p => { setSelectedParam(p); }}
              historyRange={historyRange}
              onRangeChange={setHistoryRange}
              paramCfg={paramCfg}
              anchorDate={anchorDate}
              onAnchorChange={setAnchorDate}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              locale={i18n.language.startsWith('es') ? 'es-MX' : 'en-US'}
            />
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={onClose} className="primary-button" style={{ padding: '8px 24px', fontSize: 13 }}>
            {t('sensorModal.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tab 1: Overview ──────────────────────────────────────────────
function OverviewTab({
  metrics, selectedParam, onSelectParam, depths,
}: {
  depths: number[];
  metrics: Record<string, number>;
  selectedParam: ParamKey;
  onSelectParam: (p: ParamKey) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        {PARAM_CONFIG.map(p => {
          // Promediar SOLO las profundidades que reportaron. El sensor del
          // piloto no manda las 9 métricas (no trae calcio, por ejemplo), y
          // con `?? 0` cada métrica ausente salía como "0.0" con badge ALERT
          // en rojo: un dato inventado, imposible de distinguir de un cero
          // real medido. Sin ninguna lectura la tarjeta va en gris con "—".
          const present = depths
            .map(d => metrics[`${p.key}_${d}`])
            .filter((v): v is number => v != null);
          const avg = present.length > 0 ? present.reduce((a, b) => a + b, 0) / present.length : null;
          const status = avg != null ? getStatus(avg, p.lo, p.hi) : null;
          const color = status ? STATUS_COLOR[status] : 'var(--gray)';
          const selected = selectedParam === p.key;
          const pct = avg != null
            ? Math.min(100, Math.max(0, ((avg - p.lo * 0.5) / (p.hi * 1.5 - p.lo * 0.5)) * 100))
            : null;
          const loPct = ((p.lo - p.lo * 0.5) / (p.hi * 1.5 - p.lo * 0.5)) * 100;
          const hiPct = ((p.hi - p.lo * 0.5) / (p.hi * 1.5 - p.lo * 0.5)) * 100;

          return (
            <div
              key={p.key}
              onClick={() => onSelectParam(p.key as ParamKey)}
              style={{
                padding: '14px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
                border: selected ? '1.5px solid var(--green)' : '1px solid var(--border)',
                background: selected ? 'var(--green-bg)' : 'var(--white)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ParamIcon icon={p.icon} size={16} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--dark)' }}>{t(`soil.params.${p.key}`)}</span>
                </div>
                <span style={{ fontSize: 9, fontWeight: 700, color, background: `${color}18`, padding: '2px 6px', borderRadius: 10 }}>
                  {status ? status.toUpperCase() : t('soil.modal.noData')}
                </span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color, marginBottom: 8 }}>
                {avg != null ? avg.toFixed(1) : '—'}
                {avg != null && <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 2, color: 'var(--gray)' }}>{p.unit}</span>}
              </div>
              {/* Mini range bar */}
              <div style={{ height: 5, background: 'var(--border)', borderRadius: 3, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', left: `${loPct}%`, right: `${100 - hiPct}%`, top: 0, bottom: 0, background: 'var(--green)', opacity: 0.25, borderRadius: 3 }} />
                {pct != null && <div style={{ position: 'absolute', left: `calc(${pct}% - 3px)`, top: -1, width: 6, height: 7, background: color, borderRadius: 3 }} />}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span style={{ fontSize: 8, color: 'var(--gray)' }}>{t('soil.modal.overview.minThreshold')}: {p.lo}</span>
                <span style={{ fontSize: 8, color: 'var(--gray)' }}>{t('soil.modal.overview.maxThreshold')}: {p.hi}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Depth breakdown for selected param */}
      <DepthBreakdown metrics={metrics} selectedParam={selectedParam} depths={depths} />
    </div>
  );
}

function DepthBreakdown({ metrics, selectedParam, depths }: { metrics: Record<string, number>; selectedParam: ParamKey; depths: number[] }) {
  const { t } = useTranslation();
  const cfg = PARAM_CONFIG.find(p => p.key === selectedParam)!;
  // null y NO 0 en las profundidades sin lectura — un hueco no es un cero.
  const values = depths.map((d, i) => ({ depth: d, value: metrics[`${selectedParam}_${d}`] ?? null, i }));
  const maxVal = Math.max(...values.map(v => v.value ?? 0), cfg.hi) * 1.1;

  return (
    <div style={{ marginTop: 24, padding: '16px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--dark)', marginBottom: 14 }}>
        {cfg.icon} {t(`soil.params.${selectedParam}`)} {t('soil.modal.overview.byDepth')}
      </div>
      {/* La nota sale SÓLO si hay alguna capa topada: explicar un tope que no
          está ocurriendo es ruido, y esconderlo cuando sí ocurre es peor. */}
      {values.some(v => esTopeDeSensor(selectedParam, v.value)) && (
        <div style={{ fontSize: 10.5, color: 'var(--gray)', lineHeight: 1.5, marginBottom: 12, padding: '8px 10px', background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <strong>≥99 %</strong> es el tope de la sonda, no una medición: el
          equipo reporta 99 % siempre que el cálculo sale más alto, así que el
          valor real puede ser cualquiera por encima de ése. Se corrige
          recalibrando la sonda contra el suelo del sitio.
        </div>
      )}
      {values.map(({ depth, value, i }) => {
        const { lo, hi } = getDepthThreshold(cfg, i);
        const status = value != null ? getStatus(value, lo, hi) : null;
        const color = status ? STATUS_COLOR[status] : 'var(--gray)';
        const pct = value != null ? Math.min(100, (value / maxVal) * 100) : 0;
        const loPct = Math.min(100, (lo / maxVal) * 100);
        const hiPct = Math.min(100, (hi / maxVal) * 100);
        return (
          <div key={depth} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ width: 36, fontSize: 10, color: 'var(--gray)', flexShrink: 0, textAlign: 'right' }}>{depth}cm</span>
            <div style={{ flex: 1, height: 10, background: 'var(--border)', borderRadius: 5, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', left: `${loPct}%`, right: `${100 - hiPct}%`, top: 0, bottom: 0, background: 'var(--green)', opacity: 0.2 }} />
              <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 5, transition: 'width 0.3s' }} />
            </div>
            <span style={{ width: 52, fontSize: 10, fontWeight: 600, color, flexShrink: 0 }}>
              {value != null ? `${formatLectura(selectedParam, value)} ${cfg.unit}` : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Tab 2: Depth Profile ─────────────────────────────────────────
function DepthProfileTab({
  selectedParam, onSelectParam, paramCfg, depthValues, depths,
}: {
  selectedParam: ParamKey;
  onSelectParam: (p: ParamKey) => void;
  paramCfg: typeof PARAM_CONFIG[number];
  depths: number[];
  depthValues: Array<number | null>;
}) {
  const { t } = useTranslation();
  return (
    <div>
      {/* Param pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {PARAM_CONFIG.map(p => (
          <button
            key={p.key}
            onClick={() => onSelectParam(p.key as ParamKey)}
            style={{
              padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
              background: selectedParam === p.key ? 'var(--green)' : 'var(--border)',
              color: selectedParam === p.key ? '#fff' : 'var(--gray)',
              transition: 'all 0.15s',
            }}
          >
            <ParamIcon icon={p.icon} size={13} />{' '}{t(`soil.params.${p.key}`)}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div style={{ background: 'var(--bg)', borderRadius: 12, padding: '16px', border: '1px solid var(--border)' }}>
        <DepthProfileChart
          depths={depths}
          values={depthValues}
          lo={paramCfg.lo}
          hi={paramCfg.hi}
          unit={paramCfg.unit}
          label={`${t(`soil.params.${selectedParam}`)} ${t('soil.modal.depthProfile.title')}`}
          actualLabel={t('soil.modal.depthProfile.actual')}
          minLabel={t('soil.modal.depthProfile.min')}
          maxLabel={t('soil.modal.depthProfile.max')}
          depthThresholds={(paramCfg as { depthThresholds?: ReadonlyArray<{ lo: number; hi: number }> }).depthThresholds}
        />
      </div>

      {/* Quick stats */}
      <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
        {[
          // Promedia SOLO las profundidades con lectura: incluir los huecos
          // como 0 hundiría el promedio y mostraría un número falso.
          { label: t('soil.modal.depthProfile.actual') + ' (avg)', value: (() => {
              const present = depthValues.filter((v): v is number => v != null);
              return present.length > 0
                ? (present.reduce((a, b) => a + b, 0) / present.length).toFixed(1)
                : '—';
            })() },
          { label: t('soil.modal.depthProfile.min'), value: paramCfg.lo },
          { label: t('soil.modal.depthProfile.max'), value: paramCfg.hi },
        ].map(item => (
          <div key={item.label} style={{ flex: 1, padding: '10px 14px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)', textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--dark)' }}>{item.value}<span style={{ fontSize: 10, marginLeft: 2 }}>{paramCfg.unit}</span></div>
            <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 2 }}>{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab 3: Heat Map ──────────────────────────────────────────────
function HeatMapTab({ metrics, depths }: { metrics: Record<string, number>; depths: number[] }) {
  const { t } = useTranslation();

  const getCellColor = (status: 'good' | 'warn' | 'alert') => {
    if (status === 'good')  return { bg: '#dcfce7', text: '#166534' };
    if (status === 'warn')  return { bg: '#fef9c3', text: '#854d0e' };
    return                         { bg: '#fee2e2', text: '#991b1b' };
  };

  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--gray)', marginBottom: 14 }}>{t('soil.modal.heatMap.title')}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--gray)', fontWeight: 600, fontSize: 10, borderBottom: '1px solid var(--border)' }}>
                {t('soil.params.humidity').slice(0, 0)}Parameter
              </th>
              {depths.map(d => (
                <th key={d} style={{ padding: '8px 10px', textAlign: 'center', color: 'var(--gray)', fontWeight: 600, fontSize: 10, borderBottom: '1px solid var(--border)' }}>
                  {d}cm
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PARAM_CONFIG.map(p => (
              <tr key={p.key}>
                <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--dark)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>
                  <ParamIcon icon={p.icon} size={13} />{' '}{t(`soil.params.${p.key}`)}
                </td>
                {depths.map((d, di) => {
                  // Celda sin lectura: gris y "—". Pintarla del color de un
                  // status calculado sobre un cero inventado hacía ver el mapa
                  // de calor entero como si el sensor midiera en todas partes.
                  const val = metrics[`${p.key}_${d}`] ?? null;
                  const { lo, hi } = getDepthThreshold(p, di);
                  const cell = val != null
                    ? getCellColor(getStatus(val, lo, hi))
                    : { bg: 'var(--bg)', text: 'var(--gray)' };
                  return (
                    <td key={d} style={{ padding: '8px 6px', textAlign: 'center', background: cell.bg, color: cell.text, fontWeight: 600, borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--white)' }}>
                      {formatLectura(p.key, val)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
        {([
          { bg: '#dcfce7', text: '#166534', label: t('soil.modal.heatMap.inRange') },
          { bg: '#fef9c3', text: '#854d0e', label: t('soil.modal.heatMap.nearLimit') },
          { bg: '#fee2e2', text: '#991b1b', label: t('soil.modal.heatMap.outOfRange') },
        ] as const).map(item => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 14, height: 14, borderRadius: 3, background: item.bg, border: `1px solid ${item.text}30` }} />
            <span style={{ fontSize: 10, color: 'var(--gray)' }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab 4: Historical ────────────────────────────────────────────
function HistoricalTab({
  historyData, selectedParam, onSelectParam, historyRange, onRangeChange, paramCfg,
  anchorDate, onAnchorChange, rangeStart, rangeEnd, locale, loading, error,
}: {
  historyData: Array<{ ts: string; value: number }>;
  loading: boolean;
  error: boolean;
  selectedParam: ParamKey;
  onSelectParam: (p: ParamKey) => void;
  historyRange: HistoryRange;
  onRangeChange: (r: HistoryRange) => void;
  paramCfg: typeof PARAM_CONFIG[number];
  anchorDate: Date;
  onAnchorChange: (d: Date) => void;
  rangeStart: Date;
  rangeEnd: Date;
  locale: string;
}) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const ranges: { key: HistoryRange; label: string }[] = [
    { key: 'year',  label: t('soil.modal.historical.year') },
    { key: 'month', label: t('soil.modal.historical.month') },
    { key: 'week',  label: t('soil.modal.historical.week') },
    { key: 'day',   label: t('soil.modal.historical.day') },
  ];

  const avg = historyData.length > 0
    ? historyData.reduce((sum, d) => sum + d.value, 0) / historyData.length
    : 0;
  const minVal = historyData.length > 0 ? Math.min(...historyData.map(d => d.value)) : 0;
  const maxVal = historyData.length > 0 ? Math.max(...historyData.map(d => d.value)) : 0;

  return (
    <div>
      {/* Param pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {PARAM_CONFIG.map(p => (
          <button
            key={p.key}
            onClick={() => onSelectParam(p.key as ParamKey)}
            style={{
              padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
              background: selectedParam === p.key ? 'var(--green)' : 'var(--border)',
              color: selectedParam === p.key ? '#fff' : 'var(--gray)',
              transition: 'all 0.15s',
            }}
          >
            <ParamIcon icon={p.icon} size={13} />{' '}{t(`soil.params.${p.key}`)}
          </button>
        ))}
      </div>

      {/* Range buttons */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {ranges.map(r => (
          <button
            key={r.key}
            onClick={() => onRangeChange(r.key)}
            style={{
              padding: '6px 16px', borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer', fontSize: 12,
              fontWeight: historyRange === r.key ? 600 : 400,
              background: historyRange === r.key ? 'var(--dark)' : 'var(--white)',
              color: historyRange === r.key ? '#fff' : 'var(--gray)',
              transition: 'all 0.15s',
            }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Period picker — jump the selected range to any point in the past */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {historyRange === 'day' && (
          <input
            type="date"
            value={toDateInputValue(anchorDate)}
            onChange={e => e.target.value && onAnchorChange(parseDateInput(e.target.value))}
            className="settings-input"
            style={{ width: 'auto' }}
          />
        )}
        {historyRange === 'week' && (
          <>
            <span style={{ fontSize: 11, color: 'var(--gray)' }}>{t('soil.modal.historical.pickWeek')}</span>
            <input
              type="date"
              value={toDateInputValue(anchorDate)}
              onChange={e => e.target.value && onAnchorChange(parseDateInput(e.target.value))}
              className="settings-input"
              style={{ width: 'auto' }}
            />
          </>
        )}
        {historyRange === 'month' && (
          <input
            type="month"
            value={toMonthInputValue(anchorDate)}
            onChange={e => e.target.value && onAnchorChange(parseMonthInput(e.target.value))}
            className="settings-input"
            style={{ width: 'auto' }}
          />
        )}
        {historyRange === 'year' && (
          <select
            value={anchorDate.getFullYear()}
            onChange={e => onAnchorChange(new Date(Number(e.target.value), anchorDate.getMonth(), anchorDate.getDate()))}
            className="settings-input"
            style={{ width: 'auto' }}
          >
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--dark)' }}>
          {formatPeriodLabel(historyRange, rangeStart, rangeEnd, locale)}
        </span>
      </div>

      {/* Chart. Sin datos NO se dibuja: antes esta pestaña pintaba una línea
          plana en cero para cualquier sensor real, indistinguible de "tu
          humedad promedio fue 0%". */}
      <div style={{ background: 'var(--bg)', borderRadius: 12, padding: '16px', border: '1px solid var(--border)', marginBottom: 16 }}>
        {loading ? (
          <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--gray)' }}>
            {t('soil.modal.historical.loading')}
          </div>
        ) : historyData.length >= 2 ? (
          <HistoryChart
            data={historyData}
            unit={paramCfg.unit}
            lo={paramCfg.lo}
            hi={paramCfg.hi}
            color="var(--green)"
          />
        ) : (
          <div style={{ height: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, textAlign: 'center', padding: '0 24px' }}>
            <span style={{ fontSize: 13, color: 'var(--gray)' }}>
              {error ? t('soil.modal.historical.error') : t('soil.modal.historical.empty')}
            </span>
            <span style={{ fontSize: 11, color: 'var(--gray)' }}>
              {formatPeriodLabel(historyRange, rangeStart, rangeEnd, locale)}
            </span>
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12 }}>
        {[
          // Sin lecturas se muestra "—", no 0.0: un promedio de cero es una
          // afirmación sobre el suelo del cliente que nadie midió.
          { label: 'Average', value: historyData.length > 0 ? avg.toFixed(1) : '—' },
          { label: 'Min',     value: historyData.length > 0 ? minVal.toFixed(1) : '—' },
          { label: 'Max',     value: historyData.length > 0 ? maxVal.toFixed(1) : '—' },
          { label: 'Optimal', value: `${paramCfg.lo}–${paramCfg.hi}` },
        ].map(item => (
          <div key={item.label} style={{ flex: 1, padding: '10px 14px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)', textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--dark)' }}>
              {item.value}<span style={{ fontSize: 10, marginLeft: 2, color: 'var(--gray)' }}>{item.label === 'Optimal' ? '' : paramCfg.unit}</span>
            </div>
            <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 2 }}>{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
